import { css, useTheme } from '@emotion/react';
import { Drag } from '@icon-park/react';
import cn from 'classnames';
import { type MemoExoticComponent, type MouseEventHandler, memo, type ReactNode } from 'react';

import type { AvatarShape } from '@didaskill/types/web';
import { useFocus } from '@didaskill/ui/utils';

import { removeButtonAppearance } from '../../../utils/style';
import { ICON_SIZES } from '../../general/icon/constants';
import { AvatarInline } from '../avatar-inline/AvatarInline';
import * as styles from './AvatarBox.styles';

const DRAGGABLE_CLASSNAME = 'DRAGGABLE';

export type AvatarBoxProps = {
  /** User to whom the avatar belongs. */
  user: AvatarShape;
  /** Status of the user. */
  status?: ReactNode;
  /** Callback when clicking on the box itself. Do not use together with action. */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** Show component as selected. */
  isSelected?: boolean;
  /** Icon for the right button. */
  action?: ReactNode;
  /** Show component as draggable. */
  isDraggable?: boolean;
};

export const AvatarBoxBase = ({
  user,
  status = null,
  onClick = null,
  isSelected = false,
  action = null,
  isDraggable = false,
  ...props
}: AvatarBoxProps) => {
  const { isFocused, ...focusProps } = useFocus<HTMLButtonElement>(props);
  const theme = useTheme();

  const avatarBox = (
    <div className={cn({ isSelected, isFocused })} css={styles.container}>
      {isDraggable && (
        <Drag
          className={DRAGGABLE_CLASSNAME}
          size={ICON_SIZES.MEDIUM}
          fill={[theme.colors.gray[300]]}
        />
      )}

      <AvatarInline
        size={AvatarInline.SIZES.MEDIUM}
        user={user}
        status={status}
        css={css`
          flex: 1;
        `}
      />

      {!onClick && action}
    </div>
  );

  return onClick ? (
    <button
      {...focusProps}
      type="button"
      css={[removeButtonAppearance, styles.button]}
      onClick={onClick}
    >
      {avatarBox}
    </button>
  ) : (
    avatarBox
  );
};

AvatarBoxBase.displayName = 'AvatarBox';

export const AvatarBox: MemoExoticComponent<typeof AvatarBoxBase> & {
  DRAGGABLE_CLASSNAME?: typeof DRAGGABLE_CLASSNAME;
} = memo(AvatarBoxBase);

AvatarBox.DRAGGABLE_CLASSNAME = DRAGGABLE_CLASSNAME;
